// ============================================================================
// FIX-CAPTURE-TRANCHE-2B-W2 — voice-prompt clip resolver.
//
// The server (get_capture_cards) hands every capture card a `clip_path` such as
//   voice-prompts/coping-affected/lo.v1.mp3
// This module turns that path into a playable URL through the SAME two layers GEN_AUDIOS uses:
//   * signing  — getSignedAudioUrl() (signedAudio.ts), private bucket, 3600 s TTL;
//   * caching  — getCachedAudioUrl() (audioCache.ts), bytes in IndexedDB keyed by a stable key.
// The cache key is the FULL VERSIONED PATH, so a re-recorded clip (…/lo.v2.mp3) is a new key and
// nothing has to bump AUDIO_PACK_VERSION; the old blob is simply never asked for again.
//
// lo ↔ la: the estate already bridges the Lao language-code split twice ad hoc (SD-T38:
// onboardingVideo.ts:22-33 for the video asset; duplicate `_la`/`_lo` uploads in
// voice-responses/system). This resolver adds the bridge ONCE more, in its own candidate list
// (the onboardingVideo.ts languageCandidates() shape), so a stray `la` object still plays and no
// component needs a fourth copy. Nothing existing is deleted here — that consolidation is SD-T38.
//
// Additive: returns null, never throws. No component renders anything from this yet (W3).
// ============================================================================

import { getCachedAudioUrl } from './audioCache';
import { getSignedAudioUrl, SIGNED_AUDIO_TTL_SECONDS, storagePathFromStored } from './signedAudio';

export const VOICE_PROMPTS_BUCKET = 'voice-prompts';

/** `voice-prompts/coping-affected/lo.v1.mp3` → `{ key: 'coping-affected', lang: 'lo', file: 'lo.v1.mp3' }` */
export function parseVoicePromptPath(clipPath: string | null | undefined): { objectPath: string; key: string; lang: string; file: string } | null {
  const objectPath = storagePathFromStored(clipPath, VOICE_PROMPTS_BUCKET);
  if (!objectPath) return null;
  const parts = objectPath.split('/');
  if (parts.length < 2) return null;
  const file = parts[parts.length - 1];
  const key = parts.slice(0, -1).join('/');
  const lang = file.split('.')[0] ?? '';
  if (!key || !lang) return null;
  return { objectPath, key, lang, file };
}

/**
 * Ordered object paths to try for one clip path. The manifest path comes first; if its language
 * segment is `lo` or `la`, the twin spelling follows (the single lo↔la bridge for this asset family).
 */
export function voicePromptCandidates(clipPath: string | null | undefined): string[] {
  const parsed = parseVoicePromptPath(clipPath);
  if (!parsed) return [];
  const out = new Set<string>([parsed.objectPath]);
  const twin = parsed.lang === 'lo' ? 'la' : parsed.lang === 'la' ? 'lo' : null;
  if (twin) {
    const twinFile = twin + parsed.file.slice(parsed.lang.length);
    out.add(`${parsed.key}/${twinFile}`);
  }
  return Array.from(out);
}

/** Stable IndexedDB cache key: the full versioned path, bucket-prefixed. */
export function voicePromptCacheKey(clipPath: string | null | undefined): string | null {
  const parsed = parseVoicePromptPath(clipPath);
  return parsed ? `${VOICE_PROMPTS_BUCKET}/${parsed.objectPath}` : null;
}

/**
 * Cache-first playable URL for a manifest clip path.
 *  - cache HIT  → local blob: URL, zero Supabase calls;
 *  - cache MISS → sign the first candidate that resolves (manifest path, then its lo↔la twin),
 *                 fetch + store the bytes, return a local URL (or the signed URL if storing fails);
 *  - nothing resolves / empty path → null.
 */
export async function getVoicePromptUrl(clipPath: string | null | undefined): Promise<string | null> {
  const key = voicePromptCacheKey(clipPath);
  if (!key) return null;
  const candidates = voicePromptCandidates(clipPath);
  return getCachedAudioUrl(key, async () => {
    for (const objectPath of candidates) {
      const url = await getSignedAudioUrl(objectPath, VOICE_PROMPTS_BUCKET, SIGNED_AUDIO_TTL_SECONDS);
      if (url) return url;
    }
    return null;
  });
}
